/**
 * "Move with value": a node that slides along a row of marker nodes.
 *
 * The author draws the markers (COST, MFN, FLOOR …) wherever they like and
 * picks them as the moving node's stops. At run time every stop's number is
 * read from its own text, the stops are ordered by that number, and the moving
 * node is centred between the two stops its own number falls between — so it
 * always lands in the right gap, however unevenly the markers were spaced.
 *
 * Stops must be SIBLINGS of the moving node (same parent), so their x values
 * share one coordinate space and no absolute geometry is needed.
 */

import type { DesignNode, SlideOverlapMode, TextDataBinding } from '../design-builder.types';

/** Reads a bound text's live value; null while it is loading or missing. */
export type BoundValueReader = (binding: TextDataBinding) => number | string | null;
/** A text formula's result; null while it cannot be worked out. */
export type FormulaValueReader = (node: DesignNode) => number | null;

export interface SlideStop {
  node: DesignNode;
  value: number;
  /** Centre x in the parent's space. */
  centerX: number;
}

/**
 * The first number in a piece of text — `"$6,800"` → 6800, `"(1.5)"` → 1.5,
 * `"-3%"` → -3. Thousands separators are ignored.
 */
export function parseNumber(text: string): number | null {
  const match = text.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
}

/** A TEXT node's number: its formula's result, the live value when bound, otherwise what is typed. */
function textNumber(node: DesignNode, read: BoundValueReader, readFormula?: FormulaValueReader): number | null {
  if (node.formula?.expression && readFormula) return readFormula(node);
  if (node.dataBinding) {
    const value = read(node.dataBinding);
    if (value === null || value === undefined || value === '') return null;
    const numeric = typeof value === 'number' ? value : parseNumber(String(value));
    return numeric !== null && Number.isFinite(numeric) ? numeric : null;
  }
  return parseNumber(node.text?.value ?? '');
}

function textsIn(node: DesignNode): DesignNode[] {
  if (node.type === 'TEXT') return [node];
  return (node.children ?? []).flatMap(textsIn);
}

/**
 * The number a node stands for. A group usually holds a price pill, a label
 * and a percentage, so a bound text wins, then a text that is not a
 * percentage, then any text with a number in it.
 */
export function nodeNumber(node: DesignNode, read: BoundValueReader, readFormula?: FormulaValueReader): number | null {
  const texts = textsIn(node);
  const bound = texts.find((text) => text.dataBinding || (readFormula && text.formula?.expression));
  if (bound) return textNumber(bound, read, readFormula);
  const isPercent = (text: DesignNode) => (text.text?.value ?? '').trim().endsWith('%');
  const ordered = [...texts.filter((text) => !isPercent(text)), ...texts.filter(isPercent)];
  for (const text of ordered) {
    const value = textNumber(text, read, readFormula);
    if (value !== null) return value;
  }
  return null;
}

/** The node's stops that resolve to a number, ordered by that number. */
export function slideStops(node: DesignNode, siblings: readonly DesignNode[], read: BoundValueReader, readFormula?: FormulaValueReader): SlideStop[] {
  const ids = node.slide?.stopIds ?? [];
  return siblings
    .filter((sibling) => sibling.id !== node.id && ids.includes(sibling.id))
    .map((sibling) => ({ node: sibling, value: nodeNumber(sibling, read, readFormula), centerX: sibling.x + sibling.width / 2 }))
    .filter((stop): stop is SlideStop => stop.value !== null)
    .sort((a, b) => a.value - b.value);
}

/**
 * Where the moving node's left edge goes, or null to leave it where it was
 * drawn (no value yet, or fewer than two usable stops). A value outside the
 * stops is pinned to the nearest end.
 */
export function slideLeft(node: DesignNode, siblings: readonly DesignNode[], read: BoundValueReader, readFormula?: FormulaValueReader): number | null {
  return centredLeft(node, slideStops(node, siblings, read, readFormula), nodeNumber(node, read, readFormula));
}

/** Gap kept between the moving layer and a stop it would have covered. */
const OVERLAP_GAP = 6;

/** `slide.avoidOverlap`, defaulting to "nearest": the stops stay, the value moves to the nearest free spot. */
function overlapMode(node: DesignNode): SlideOverlapMode {
  return node.slide?.avoidOverlap ?? 'nearest';
}

/**
 * Where the moving node goes (x and y), or null to leave it where it was
 * drawn. Past the last (first) stop it sits at that end of the row and the
 * end stop steps aside for it (stopShift). Otherwise the stops stay put;
 * landing on top of one (e.g. its value equals the stop's, or the stops are
 * close together), the value moves as
 * `slide.avoidOverlap` says: "nearest" (default) slides it sideways to the
 * closest spot in the row where it - with its pill, caption and % - overlaps
 * no stop's pill / caption / %; "beside" / "above" / "below" step it off the
 * stop's own layer.
 */
export function slidePosition(
  node: DesignNode,
  siblings: readonly DesignNode[],
  read: BoundValueReader,
  readFormula?: FormulaValueReader,
): { x: number; y: number } | null {
  const stops = slideStops(node, siblings, read, readFormula);
  const value = nodeNumber(node, read, readFormula);
  const left = centredLeft(node, stops, value);
  if (left === null || value === null) return null;
  const backdrops = backdropsOf(node, siblings);
  // The value with the pill behind it.
  const union = boundsOf([node, ...backdrops]);
  const beyond = beyondEnd(stops, value);
  const mode = overlapMode(node);
  if (mode !== 'nearest' && !beyond) {
    const cleared = clearOfStops({ ...union, x: union.x + (left - node.x) }, value, stops, mode);
    return { x: node.x + (cleared.x - union.x), y: node.y + (cleared.y - union.y) };
  }

  // The whole moving piece - value, pill, and the caption / % it carries -
  // must stay clear of every stop's whole column.
  const { piece, carried } = movingPiece(node, backdrops, siblings);
  const row = rowOf(stops);
  const width = piece.width;
  // Past an end stop: at that end of the row; the end stop makes room.
  if (beyond) return { x: node.x + ((beyond === 'high' ? row.right - width : row.left) - piece.x), y: node.y };
  const columns = rowColumns(markerColumns(node, siblings, carried, read, readFormula), piece);
  const left0 = Math.min(row.left, ...columns.map((column) => column.box.x));
  const right0 = Math.max(row.right, ...columns.map((column) => column.box.x + column.box.width));
  const hits = (x: number) => columns.filter((column) => x < column.box.x + column.box.width + OVERLAP_GAP && column.box.x - OVERLAP_GAP < x + width);

  // On its own number.
  const wanted = Math.max(row.left, Math.min(piece.x + (left - node.x), row.right - width));
  const at = (x: number) => ({ x: node.x + (x - piece.x), y: node.y });

  // The VALUES decide where it may go: between the stop just below it and the
  // stop just above it (COST < 163,547 < MFN → between COST and MFN), never
  // past either. Within that gap it takes the free spot nearest its number;
  // only a gap with no room falls back to stepping aside (below).
  const below = [...stops].reverse().find((stop) => stop.value <= value);
  const above = stops.find((stop) => stop.value > value);
  const columnOf = (stop: SlideStop | undefined) => (stop ? columns.find((column) => column.ids.includes(stop.node.id)) : undefined);
  const lowColumn = columnOf(below);
  const highColumn = columnOf(above);
  const lo = lowColumn ? lowColumn.box.x + lowColumn.box.width + OVERLAP_GAP : left0;
  const hi = highColumn ? highColumn.box.x - OVERLAP_GAP - width : right0 - width;
  if (lo <= hi + 0.5) {
    const inGap = (x: number) => x >= lo - 0.5 && x <= hi + 0.5;
    const clamped = Math.max(lo, Math.min(wanted, hi));
    // Other markers inside the gap (a TARGET between COST and MFN, say): the free spot nearest the number.
    const spots = [clamped, lo, hi, ...columns.flatMap((column) => [column.box.x - OVERLAP_GAP - width, column.box.x + column.box.width + OVERLAP_GAP])]
      .filter((x) => inGap(x) && !hits(x).length);
    if (spots.length) return at(spots.reduce((best, x) => (Math.abs(x - wanted) < Math.abs(best - wanted) ? x : best)));
  }

  const firstHits = hits(wanted);
  if (!firstHits.length) return at(wanted);

  // Covering a marker: a smaller value goes to its left, a bigger one to its
  // right (same number: whichever side it already leans to), and keeps going
  // that way past any marker it lands on next.
  const center = wanted + width / 2;
  const hit = firstHits.reduce((a, b) => Math.abs(a.box.x + a.box.width / 2 - center) <= Math.abs(b.box.x + b.box.width / 2 - center) ? a : b);
  const hitCenter = hit.box.x + hit.box.width / 2;
  const dir: -1 | 1 = hit.value !== null && hit.value !== value ? (value < hit.value ? -1 : 1) : (center <= hitCenter ? -1 : 1);
  const place = (side: -1 | 1) => {
    let x = wanted;
    for (let pass = 0; pass <= columns.length; pass++) {
      const blocking = hits(x);
      if (!blocking.length) break;
      x = side < 0
        ? Math.min(...blocking.map((column) => column.box.x)) - OVERLAP_GAP - width
        : Math.max(...blocking.map((column) => column.box.x + column.box.width)) + OVERLAP_GAP;
    }
    return x;
  };
  const inside = (x: number) => x >= left0 - 0.5 && x + width <= right0 + 0.5;
  let x = place(dir);
  // No room that way inside the row: the other way, if that fits.
  if (!inside(x)) {
    const other = place(dir < 0 ? 1 : -1);
    if (inside(other)) x = other;
  }
  return at(x);
}

/**
 * Sideways shift for an END stop - and the layers stacked on it (its pill,
 * caption, %) - while a moving sibling is past it: Best steps left to make
 * room for a value above it at the row's right end, Cost steps right for one
 * below it. A neighbour it would then run into steps along too. Every other
 * stop stays put. Null = stays where it was drawn.
 */
export function stopShift(
  node: DesignNode,
  siblings: readonly DesignNode[],
  read: BoundValueReader,
  readFormula?: FormulaValueReader,
): { x: number; y: number } | null {
  for (const mover of siblings) {
    if (mover.id === node.id || !mover.slide?.stopIds.length || overlapMode(mover) === 'none') continue;
    const stops = slideStops(mover, siblings, read, readFormula);
    const value = nodeNumber(mover, read, readFormula);
    const beyond = value === null ? null : beyondEnd(stops, value);
    if (!beyond) continue;
    const { piece, carried } = movingPiece(mover, backdropsOf(mover, siblings), siblings);
    if (carried.has(node.id)) continue;
    const columns = rowColumns(markerColumns(mover, siblings, carried, read, readFormula), piece);
    if (!columns.some((column) => column.ids.includes(node.id))) continue;
    const row = rowOf(stops);
    // Walk inward from the end the value sits at, pushing each column clear of the one before.
    const high = beyond === 'high';
    const sorted = [...columns].sort((a, b) => high ? (b.box.x + b.box.width) - (a.box.x + a.box.width) : a.box.x - b.box.x);
    let edge = high ? row.right - piece.width : row.left + piece.width;
    for (const column of sorted) {
      const { x, width } = column.box;
      const dx = high ? Math.min(0, edge - OVERLAP_GAP - (x + width)) : Math.max(0, edge + OVERLAP_GAP - x);
      if (column.ids.includes(node.id)) return dx ? { x: node.x + dx, y: node.y } : null;
      if (!dx) break;
      edge = high ? x + dx : x + width + dx;
    }
  }
  return null;
}

/** The moving node with everything it carries, as one box. */
function movingPiece(node: DesignNode, backdrops: readonly DesignNode[], siblings: readonly DesignNode[]): { piece: Box; carried: Set<string> } {
  const union = boundsOf([node, ...backdrops]);
  const carried = carriedIds(node, backdrops, siblings);
  const piece = boundsOf([union, ...siblings.filter((item) => item.id !== node.id && carried.has(item.id) && item.visible !== false && stackedWith(item, union))]);
  return { piece, carried };
}

/** Only the stop columns that share the row with the moving piece can be in its way. */
function rowColumns<T extends { box: Box }>(columns: T[], piece: Box): T[] {
  return columns.filter((column) => column.box.y < piece.y + piece.height && piece.y < column.box.y + column.box.height);
}

/** Everything that travels with the moving node: itself, its pill, its caption / %, "Also move" layers, its group's members. */
function carriedIds(node: DesignNode, backdrops: readonly DesignNode[], siblings: readonly DesignNode[]): Set<string> {
  return new Set([
    node.id, ...(node.slide?.followerIds ?? []), ...(node.memberIds ?? []),
    ...backdrops.map((item) => item.id), ...stackedOf(node, siblings).map((item) => item.id),
  ]);
}

/**
 * Price markers next to the moving layer: its stops, plus any other number
 * text (e.g. a TARGET not ticked as a stop) - the value must not cover those
 * either. Percent lines are not markers; they belong to one.
 */
function markersOf(node: DesignNode, siblings: readonly DesignNode[]): DesignNode[] {
  const stopIds = new Set(node.slide?.stopIds ?? []);
  const own = new Set([...(node.slide?.followerIds ?? []), ...(node.memberIds ?? [])]);
  return siblings.filter((sibling) =>
    sibling.id !== node.id && !sibling.slide && sibling.visible !== false && !own.has(sibling.id)
    && (stopIds.has(sibling.id) || isNumberText(sibling)));
}

function isNumberText(node: DesignNode): boolean {
  if (node.type !== 'TEXT') return false;
  const text = (node.text?.value ?? '').trim();
  if (text.endsWith('%')) return false;
  return !!node.dataBinding || !!node.formula?.expression || parseNumber(text) !== null;
}

function centreDistance(a: Box, b: Box): number {
  return Math.hypot(a.x + a.width / 2 - (b.x + b.width / 2), a.y + a.height / 2 - (b.y + b.height / 2));
}

/** Each marker with the layers stacked on it (its pill, caption, % line); a layer near two markers goes with the closer one. */
function markerColumns(
  mover: DesignNode,
  siblings: readonly DesignNode[],
  carried: ReadonlySet<string>,
  read: BoundValueReader,
  readFormula?: FormulaValueReader,
): Array<{ value: number | null; ids: string[]; box: Box }> {
  const markers = markersOf(mover, siblings).filter((marker) => !carried.has(marker.id));
  const markerIds = new Set(markers.map((marker) => marker.id));
  // Each marker is measured with the pill it sits in, so its caption / % line
  // stack against the whole badge, not just the (short) price text inside it.
  const columns = markers.map((marker) => {
    const pills = siblings.filter((item) => item.id !== marker.id && !markerIds.has(item.id) && !carried.has(item.id)
      && item.visible !== false && holds(item, marker) && item.width * item.height <= Math.max(1, marker.width * marker.height) * BACKDROP_MAX_AREA);
    return { marker, anchor: boundsOf([marker, ...pills]), items: [marker] as DesignNode[] };
  });
  for (const item of siblings) {
    if (item.id === mover.id || item.slide || carried.has(item.id) || markerIds.has(item.id) || item.visible === false) continue;
    const owners = columns.filter((column) => stackedWith(item, column.anchor));
    if (!owners.length) continue;
    const owner = owners.reduce((best, column) => centreDistance(item, column.anchor) < centreDistance(item, best.anchor) ? column : best);
    owner.items.push(item);
  }
  return columns.map((column) => ({
    value: nodeNumber(column.marker, read, readFormula),
    ids: column.items.map((item) => item.id),
    box: boundsOf(column.items),
  }));
}

function boundsOf(nodes: readonly Box[]): Box {
  const x = Math.min(...nodes.map((node) => node.x));
  const y = Math.min(...nodes.map((node) => node.y));
  const right = Math.max(...nodes.map((node) => node.x + node.width));
  const bottom = Math.max(...nodes.map((node) => node.y + node.height));
  return { x, y, width: right - x, height: bottom - y };
}

/** How much bigger than the value a shape behind it may be and still be its pill. */
const BACKDROP_MAX_AREA = 6;

/**
 * Shapes drawn right behind the moving layer — its pill / badge. They travel
 * with it automatically: a white value moved off its dark pill would vanish on
 * the card. The card's own background is far bigger, so it stays put. A
 * shape that also holds another price marker (the value was drawn on top of
 * TARGET, say) is that marker's pill, so it stays too.
 */
export function backdropsOf(node: DesignNode, siblings: readonly DesignNode[]): DesignNode[] {
  const stopIds = new Set(node.slide?.stopIds ?? []);
  const area = Math.max(1, node.width * node.height);
  const index = siblings.findIndex((sibling) => sibling.id === node.id);
  const markers = markersOf(node, siblings);
  return siblings.filter((sibling, i) =>
    sibling.id !== node.id
    && !stopIds.has(sibling.id)
    && !sibling.slide
    && sibling.visible !== false
    // Behind it (drawn earlier) and around it.
    && (index < 0 || i < index)
    && sibling.x <= node.x + 1 && sibling.y <= node.y + 1
    && sibling.x + sibling.width >= node.x + node.width - 1
    && sibling.y + sibling.height >= node.y + node.height - 1
    && sibling.width * sibling.height <= area * BACKDROP_MAX_AREA
    && !markers.some((marker) => marker.id === sibling.id || holds(sibling, marker)));
}

/** The inner box's centre lies inside the outer one. */
function holds(outer: Box, inner: Box): boolean {
  const cx = inner.x + inner.width / 2;
  const cy = inner.y + inner.height / 2;
  return cx >= outer.x && cx <= outer.x + outer.width && cy >= outer.y && cy <= outer.y + outer.height;
}

/**
 * Layers stacked on the moving layer where it was drawn - its caption, its %
 * line - travel with it without being ticked under "Also move". A layer that
 * sits closer to a price marker (a stop, or another number) is that marker's,
 * so it stays.
 */
export function stackedOf(node: DesignNode, siblings: readonly DesignNode[]): DesignNode[] {
  const backdrops = backdropsOf(node, siblings);
  const backdropIds = new Set(backdrops.map((item) => item.id));
  const union = boundsOf([node, ...backdrops]);
  const markers = markersOf(node, siblings);
  const markerIds = new Set(markers.map((marker) => marker.id));
  return siblings.filter((sibling) =>
    sibling.id !== node.id
    && !markerIds.has(sibling.id)
    && !backdropIds.has(sibling.id)
    && !sibling.slide
    && sibling.visible !== false
    && stackedWith(sibling, union)
    && markers.every((marker) => !stackedWith(sibling, marker) || centreDistance(sibling, union) < centreDistance(sibling, marker)));
}

type Box = { x: number; y: number; width: number; height: number };

function overlaps(a: Box, b: Box): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

function clearOfStops(box: Box, value: number, stops: SlideStop[], mode: Exclude<SlideOverlapMode, 'nearest'>): { x: number; y: number } {
  if (mode === 'none') return { x: box.x, y: box.y };
  // "Beside" never leaves the row of stops: past the first / last stop it
  // would hang outside the card.
  const minX = Math.min(...stops.map((stop) => stop.node.x));
  const maxX = Math.max(...stops.map((stop) => stop.node.x + stop.node.width));
  const inside = (x: number) => x >= minX - 0.5 && x + box.width <= maxX + 0.5;

  const placed = { ...box };
  let side: 1 | -1 | 0 = 0;
  let vertical: 'above' | 'below' | null = mode === 'beside' ? null : mode;
  // A few passes: stepping clear of one stop can land on its neighbour.
  for (let pass = 0; pass <= stops.length + 1; pass++) {
    const hit = stops.find((stop) => overlaps(placed, stop.node));
    if (!hit) break;
    const rect = hit.node;
    if (!vertical) {
      const right = rect.x + rect.width + OVERLAP_GAP;
      const left = rect.x - placed.width - OVERLAP_GAP;
      // Decide the side once (so it never ping-pongs between two stops):
      // right of a smaller stop, left of a bigger one — unless that side
      // leaves the row, then the other side.
      if (!side) {
        side = value < hit.value ? -1 : 1;
        if (!inside(side > 0 ? right : left)) side = side > 0 ? -1 : 1;
      }
      const x = side > 0 ? right : left;
      if (inside(x)) { placed.x = x; continue; }
      // No room beside it inside the row: drop below it instead.
      vertical = 'below';
    }
    placed.y = vertical === 'above' ? rect.y - placed.height - OVERLAP_GAP : rect.y + rect.height + OVERLAP_GAP;
  }
  return { x: placed.x, y: placed.y };
}

function centredLeft(node: DesignNode, stops: SlideStop[], value: number | null): number | null {
  if (stops.length < 2 || value === null) return null;

  const first = stops[0];
  const last = stops[stops.length - 1];
  let centerX: number;
  if (value <= first.value) centerX = first.centerX;
  else if (value >= last.value) centerX = last.centerX;
  else {
    const index = stops.findIndex((stop) => stop.value >= value);
    const low = stops[index - 1];
    const high = stops[index];
    const span = high.value - low.value;
    const t = span > 0 ? (value - low.value) / span : 0;
    centerX = low.centerX + t * (high.centerX - low.centerX);
  }
  return centerX - node.width / 2;
}

/** 'high' past the last stop, 'low' before the first, null in between (or too few stops). */
function beyondEnd(stops: SlideStop[], value: number): 'high' | 'low' | null {
  if (stops.length < 2) return null;
  if (value > stops[stops.length - 1].value) return 'high';
  if (value < stops[0].value) return 'low';
  return null;
}

/** The row the stops span: left edge of the leftmost, right edge of the rightmost. */
function rowOf(stops: SlideStop[]): { left: number; right: number } {
  return {
    left: Math.min(...stops.map((stop) => stop.node.x)),
    right: Math.max(...stops.map((stop) => stop.node.x + stop.node.width)),
  };
}

/** A layer stacked with an anchor (its caption, its % line, its pill): centred over it, close above / below. */
function stackedWith(node: DesignNode, anchor: Box): boolean {
  const centerX = node.x + node.width / 2;
  const centerY = node.y + node.height / 2;
  const anchorCenterY = anchor.y + anchor.height / 2;
  return centerX >= anchor.x - 4 && centerX <= anchor.x + anchor.width + 4
    && Math.abs(centerY - anchorCenterY) <= Math.max(3 * anchor.height, 48)
    && node.width <= anchor.width * 3;
}

/** The sliding sibling that carries this node along ("Also move", or its sliding group). */
export function slideLeaderOf(node: DesignNode, siblings: readonly DesignNode[]): DesignNode | null {
  const leaders = siblings.filter((sibling) => sibling.id !== node.id && !!sibling.slide?.stopIds.length);
  return leaders.find((leader) => !!leader.slide!.followerIds?.includes(node.id) || !!leader.memberIds?.includes(node.id))
    ?? leaders.find((leader) => backdropsOf(leader, siblings).some((backdrop) => backdrop.id === node.id))
    ?? leaders.find((leader) => stackedOf(leader, siblings).some((item) => item.id === node.id))
    ?? null;
}

/**
 * A follower's x: it moves with the sibling that lists it in `followerIds`
 * (or with a sliding group it is a member of), by the same distance, so a
 * label / % text keeps its place beside the moving value. Null = stays put.
 */
export function followPosition(
  node: DesignNode,
  siblings: readonly DesignNode[],
  read: BoundValueReader,
  readFormula?: FormulaValueReader,
): { x: number; y: number } | null {
  const leader = slideLeaderOf(node, siblings);
  if (!leader) return null;
  const moved = slidePosition(leader, siblings, read, readFormula);
  return moved === null ? null : { x: node.x + (moved.x - leader.x), y: node.y + (moved.y - leader.y) };
}
